import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  PushNotifications,
  type PushNotificationSchema,
  type Token,
} from '@capacitor/push-notifications';
import { getPlatform, hasNativePlugin } from '@tvd/cdk/platform';
import { Storage } from '@tvd/utils/storage';
import { firstValueFrom } from 'rxjs';
import { NotificationApi } from './notification-api';

const PLUGIN_NAME = 'PushNotifications';

@Injectable({ providedIn: 'root' })
export class NativePushService {
  readonly #api = inject(NotificationApi);
  readonly #router = inject(Router);
  readonly #storage = inject(Storage);

  #listenersBound = false;
  #registered = false;
  #currentDeviceId: string | null = null;

  /**
   * Request permission, register with FCM/APNs, and upsert subscription on the
   * backend. Safe to call multiple times — idempotent for the same install.
   */
  async register(): Promise<void> {
    if (!hasNativePlugin(PLUGIN_NAME)) return;

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return;

    this.#bindListeners();
    await PushNotifications.register();
    this.#registered = true;
  }

  /**
   * Delete this device's subscription from the backend. Called on logout to
   * prevent notification leak on shared devices. Must run BEFORE access token
   * is cleared so the authenticated DELETE succeeds.
   */
  unregister(): void {
    const deviceId = this.#currentDeviceId ?? this.#storage.get('DEVICE_ID');
    if (!deviceId) return;
    this.#api.deletePushSubscription({ deviceId }).subscribe({
      error: (err: unknown) =>
        console.warn('[native-push] deletePushSubscription failed', err),
    });
    this.#registered = false;
  }

  #bindListeners(): void {
    if (this.#listenersBound) return;
    this.#listenersBound = true;

    PushNotifications.addListener('registration', (token: Token) => {
      this.#onToken(token.value).catch((err) =>
        console.error('[native-push] upsert failed', err),
      );
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('[native-push] registrationError', error);
    });

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        const data = action.notification.data as
          | { actionUrl?: string }
          | undefined;
        const actionUrl = data?.actionUrl;
        if (actionUrl) {
          this.#router.navigateByUrl(actionUrl);
        }
      },
    );
  }

  async #onToken(fcmToken: string): Promise<void> {
    const platform = getPlatform();
    if (platform !== 'ios' && platform !== 'android') return;

    const deviceId = this.#resolveDeviceId();
    this.#currentDeviceId = deviceId;

    await firstValueFrom(
      this.#api.savePushSubscription({
        type: 'native',
        deviceId,
        fcmToken,
        platform,
      }),
    );
  }

  #resolveDeviceId(): string {
    const existing = this.#storage.get('DEVICE_ID');
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    this.#storage.set('DEVICE_ID', fresh);
    return fresh;
  }
}

export type { PushNotificationSchema };
